import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TrendingUp, DollarSign, Send, Loader2, AlertTriangle, Zap, Target, ArrowRight } from "lucide-react";
import { API_ENDPOINTS, buildUrl } from "@/config/apiEndpoints";
export { WeeklyReportTab } from "@/components/intelligence/WeeklyReportTab";

// Converte qualquer valor (número ou string) para string formatada com casas decimais
const fmt = (val, dec = 0) => Number(val ?? 0).toFixed(dec);

export function ForecastTab({ authAxios }) {
  const authAxiosRef = useRef(authAxios);
  authAxiosRef.current = authAxios;
  const [forecast, setForecast] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchForecast = useCallback(async () => { setLoading(true); try { const res = await authAxiosRef.current.get(buildUrl(API_ENDPOINTS.intelligence.forecast, { days })); setForecast(res.data); } catch { toast.error("Erro ao carregar previsão"); } setLoading(false); }, [days]);
  useEffect(() => { fetchForecast(); }, [fetchForecast]);
  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>;
  if (!forecast) return null;
  const confidenceColors = { high: "text-emerald-600 bg-emerald-50", medium: "text-amber-600 bg-amber-50", low: "text-red-600 bg-red-50" };
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white rounded-xl p-4 border shadow-sm"><p className="text-xs text-gray-400 uppercase">Previsão {days} dias</p><p className="text-2xl font-bold text-emerald-600 mt-1" data-testid="forecast-total">{forecast.total_forecast_revenue ? `${fmt(forecast.total_forecast_revenue)}€` : "N/A"}</p></div>
        <div className="bg-white rounded-xl p-4 border shadow-sm"><p className="text-xs text-gray-400 uppercase">Média diária</p><p className="text-2xl font-bold text-gray-800 mt-1">{forecast.avg_daily_revenue ? `${fmt(forecast.avg_daily_revenue)}€` : "N/A"}</p></div>
        <div className="bg-white rounded-xl p-4 border shadow-sm"><p className="text-xs text-gray-400 uppercase">Cresc. mensal</p><p className={`text-2xl font-bold mt-1 ${forecast.growth_rate_monthly >= 0 ? "text-emerald-600" : "text-red-600"}`}>{forecast.growth_rate_monthly >= 0 ? "+" : ""}{forecast.growth_rate_monthly}%</p></div>
        <div className="bg-white rounded-xl p-4 border shadow-sm"><p className="text-xs text-gray-400 uppercase">Confiança</p><span className={`inline-block mt-2 px-2 py-1 rounded-full text-xs font-medium ${confidenceColors[forecast.confidence] || ""}`}>{forecast.confidence?.toUpperCase()}</span><p className="text-xs text-gray-400 mt-1">{forecast.data_points} dias de dados</p></div>
      </div>
      <div className="flex items-center gap-2 text-sm"><span className="text-gray-500">Melhor dia:</span><span className="font-bold text-emerald-700">{forecast.best_day}</span><span className="mx-2 text-gray-300">|</span><span className="text-gray-500">Pior dia:</span><span className="font-bold text-red-600">{forecast.worst_day}</span></div>
      <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
        <div className="p-4 border-b flex items-center justify-between"><h3 className="font-bold text-sm text-gray-700">Previsão por dia</h3><Select value={String(days)} onValueChange={v => setDays(Number(v))}><SelectTrigger className="w-28"><SelectValue /></SelectTrigger><SelectContent position="popper" sideOffset={4}><SelectItem value="7">7 dias</SelectItem><SelectItem value="14">14 dias</SelectItem><SelectItem value="30">30 dias</SelectItem></SelectContent></Select></div>
        <div className="divide-y">{forecast.forecast?.map((day, i) => (<div key={i} className="flex items-center px-4 py-3 hover:bg-gray-50 transition"><div className="flex-1"><p className="text-sm font-medium">{day.day_name}</p><p className="text-xs text-gray-400">{day.date}</p></div><div className="text-right mr-4"><p className="text-sm font-bold text-gray-800">{fmt(day.projected_revenue)}€</p><p className="text-xs text-gray-400">{day.projected_orders} pedidos</p></div><div className="text-right mr-3"><p className="text-xs text-gray-400">{fmt(day.low_range)}€ – {fmt(day.high_range)}€</p></div><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${confidenceColors[day.confidence] || ""}`}>{day.confidence}</span></div>))}</div>
      </div>
    </div>
  );
}

export function ProfitBrainTab({ authAxios, onNavigateTo = () => {} }) {
  const authAxiosRef = useRef(authAxios);
  authAxiosRef.current = authAxios;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { authAxiosRef.current.get(API_ENDPOINTS.intelligence.profitBrain).then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false)); }, []);
  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>;
  if (!data) return null;

  const estimated = !!data.estimated_costs;
  const marginPct = Number(data.margin_pct ?? 0);
  const marginColor = marginPct >= 65 ? "text-emerald-600" : marginPct >= 50 ? "text-yellow-600" : "text-red-600";

  const actions = [];
  if (estimated) {
    actions.push({ priority: "high", text: "Sem fichas técnicas: lucro calculado com CMV padrão de 35%. Vá ao separador CMV e adicione receitas para dados reais." });
  }
  if (marginPct < 50) {
    actions.push({ priority: "high", text: `Margem crítica (${marginPct}%). Revise custos ou aumente preços — cada 1% de margem equivale a ${fmt(Number(data.total_revenue ?? 0) * 0.01)}€/mês.` });
  }
  if (data.channels?.length > 1) {
    const sorted = [...data.channels].sort((a, b) => Number(b.margin_pct) - Number(a.margin_pct));
    const best = sorted[0]; const worst = sorted[sorted.length - 1];
    if (Number(best.margin_pct) - Number(worst.margin_pct) > 5) {
      actions.push({ priority: "medium", text: `${best.channel} tem margem ${best.margin_pct}% vs ${worst.channel} com ${worst.margin_pct}%. Direcione mais pedidos para ${best.channel}.` });
    }
  }
  data.suggestions?.forEach(sg => { if (actions.length < 5) actions.push({ priority: sg.priority, text: sg.message }); });

  // Tarefas executáveis com botões de ação
  const tasks = [];
  if (estimated) {
    tasks.push({
      problem: "CMV calculado com estimativa padrão (35%) — dados imprecisos",
      impact: `Erro potencial de ±${fmt(Number(data.total_revenue ?? 0) * 0.1)}€/mês na margem real`,
      action: "Gerar fichas técnicas com IA para todos os produtos em 1 clique",
      btnLabel: "Gerar fichas com IA",
      onClick: () => onNavigateTo("cmv"),
      btnClass: "bg-violet-600 hover:bg-violet-700 text-white",
    });
  }
  if (marginPct < 50 && data.products?.length > 0) {
    const worst = [...(data.products ?? [])].sort((a, b) => Number(a.margin_pct) - Number(b.margin_pct))[0];
    tasks.push({
      problem: `Margem crítica${worst ? ` — "${worst.name}" tem apenas ${worst.margin_pct}% de margem` : ` (${marginPct}%)`}`,
      impact: `Perda estimada de ${fmt(Number(data.total_revenue ?? 0) * Math.max(0, 0.65 - marginPct / 100))}€/mês vs. meta de 65%`,
      action: "Ajustar preços ou reduzir custos dos produtos menos rentáveis",
      btnLabel: "Ver Precificação",
      onClick: () => onNavigateTo("pricing"),
      btnClass: "bg-amber-500 hover:bg-amber-600 text-white",
    });
  }
  if (data.channels?.length > 1) {
    const sorted = [...data.channels].sort((a, b) => Number(b.margin_pct) - Number(a.margin_pct));
    const best = sorted[0]; const worst = sorted[sorted.length - 1];
    const diff = Number(best.margin_pct) - Number(worst.margin_pct);
    if (diff > 8) {
      tasks.push({
        problem: `${worst.channel} tem margem ${worst.margin_pct}% — ${diff}pp abaixo de ${best.channel} (${best.margin_pct}%)`,
        impact: `Redirecionar 20% dos pedidos de ${worst.channel} para ${best.channel} pode gerar +${fmt(Number(data.total_revenue ?? 0) * diff * 0.001)}€/mês`,
        action: `Renegociar taxas de ${worst.channel} ou criar incentivos para ${best.channel}`,
        btnLabel: "Ver Precificação",
        onClick: () => onNavigateTo("pricing"),
        btnClass: "bg-blue-500 hover:bg-blue-600 text-white",
      });
    }
  }

  return (
    <div className="space-y-4">
      {estimated && (
        <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-xl" data-testid="pb-estimated-banner">
          <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-bold text-amber-800">Dados estimados · Precisão ~60%</p>
            <p className="text-xs text-amber-700 mt-0.5">Lucro calculado com CMV padrão de 35%. <strong>Adicione fichas técnicas</strong> no separador CMV para precisão real.</p>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-emerald-100 shadow-sm overflow-hidden">
        <div className="bg-emerald-600 px-5 py-3 flex items-center justify-between">
          <p className="text-xs text-emerald-100 uppercase tracking-wider font-semibold">Últimos 30 dias</p>
          {!estimated
            ? <span className="text-[10px] bg-white/20 text-white px-2 py-0.5 rounded-full font-medium">✓ Fichas técnicas reais</span>
            : <span className="text-[10px] bg-amber-400/30 text-amber-100 px-2 py-0.5 rounded-full font-medium">⚠ CMV estimado</span>}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-y md:divide-y-0 divide-gray-100">
          <div className="p-4">
            <p className="text-xs text-gray-400 uppercase tracking-wide">Faturamento</p>
            <p className="text-3xl font-black mt-1 text-gray-800" data-testid="pb-revenue">{fmt(data.total_revenue)}€</p>
            <p className="text-[10px] text-emerald-600 mt-0.5 uppercase font-semibold">Real</p>
          </div>
          <div className="p-4">
            <p className="text-xs text-gray-400 uppercase tracking-wide">Pedidos</p>
            <p className="text-3xl font-black mt-1 text-gray-800">{data.orders_count}</p>
            <p className="text-[10px] text-emerald-600 mt-0.5 uppercase font-semibold">Real</p>
          </div>
          <div className="p-4">
            <p className="text-xs text-gray-400 uppercase tracking-wide">Margem</p>
            <p className={`text-3xl font-black mt-1 ${marginColor}`}>{marginPct}%</p>
            <p className={`text-[10px] mt-0.5 uppercase font-semibold ${estimated ? "text-amber-500" : "text-emerald-600"}`}>{estimated ? "Estimado" : "Real"}</p>
          </div>
          <div className="p-4">
            <p className="text-xs text-gray-400 uppercase tracking-wide">{estimated ? "Lucro Estimado" : "Lucro"}</p>
            <p className="text-3xl font-black mt-1 text-emerald-600" data-testid="pb-profit">{fmt(data.total_profit)}€</p>
            <p className={`text-[10px] mt-0.5 uppercase font-semibold ${estimated ? "text-amber-500" : "text-emerald-600"}`}>{estimated ? "Estimado" : "Real"}</p>
          </div>
        </div>
      </div>

      {actions.length > 0 && (
        <div className="bg-white rounded-xl border-2 border-emerald-100 shadow-sm overflow-hidden" data-testid="pb-actions">
          <div className="bg-gray-800 px-4 py-3 flex items-center gap-2">
            <Zap className="w-4 h-4 text-amber-400" />
            <h3 className="font-bold text-sm text-white">O QUE FAZER AGORA</h3>
          </div>
          <div className="divide-y">
            {actions.map((a, i) => (
              <div key={i} className={`px-4 py-3 flex items-start gap-3 ${a.priority === "high" ? "bg-red-50" : a.priority === "medium" ? "bg-amber-50" : "bg-white"}`}>
                <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded shrink-0 mt-0.5 ${a.priority === "high" ? "bg-red-500 text-white" : a.priority === "medium" ? "bg-amber-500 text-white" : "bg-emerald-500 text-white"}`}>
                  {a.priority === "high" ? "AGIR" : a.priority === "medium" ? "ATENÇÃO" : "OK"}
                </span>
                <p className="text-sm text-gray-700 leading-relaxed">{a.text}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {tasks.length > 0 && (
        <div className="bg-white rounded-xl border-2 border-blue-100 shadow-sm overflow-hidden" data-testid="pb-tasks">
          <div className="bg-blue-700 px-4 py-3 flex items-center gap-2">
            <Target className="w-4 h-4 text-white" />
            <h3 className="font-bold text-sm text-white">TAREFAS RECOMENDADAS</h3>
            <span className="ml-auto text-[10px] bg-white/20 text-white px-2 py-0.5 rounded-full font-medium">{tasks.length} ação{tasks.length > 1 ? "ões" : ""}</span>
          </div>
          <div className="divide-y">
            {tasks.map((t, i) => (
              <div key={i} className="p-4 flex flex-col md:flex-row md:items-center gap-3">
                <div className="flex-1 space-y-1">
                  <p className="text-sm font-bold text-gray-800">⚠ {t.problem}</p>
                  <p className="text-xs font-semibold text-red-600">Impacto: {t.impact}</p>
                  <p className="text-xs text-gray-500 flex items-center gap-1"><ArrowRight className="w-3 h-3 shrink-0" />{t.action}</p>
                </div>
                <button
                  onClick={t.onClick}
                  className={`shrink-0 px-4 py-2 rounded-lg text-xs font-bold transition-colors flex items-center gap-1.5 ${t.btnClass}`}
                >
                  {t.btnLabel} <ArrowRight className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
        <div className="p-4 border-b"><h3 className="font-bold text-sm text-gray-700">Margem por Produto</h3></div>
        <div className="divide-y">
          {data.products?.slice(0, 15).map((p, i) => (
            <div key={i} className="flex items-center px-4 py-2 hover:bg-gray-50">
              <div className="flex-1">
                <p className="text-sm font-medium">{p.name}</p>
                <p className="text-xs text-gray-400">{p.qty}x &middot; {p.has_recipe ? <span className="text-emerald-600 font-medium">Ficha real</span> : <span className="text-amber-600">CMV estimado</span>}</p>
              </div>
              <div className="text-right mr-4">
                <p className="text-sm font-bold">{fmt(p.revenue)}€</p>
                <p className="text-xs text-emerald-600">+{fmt(p.profit)}€</p>
              </div>
              <div className={`px-2 py-1 rounded-full text-xs font-bold ${Number(p.margin_pct) >= 65 ? "bg-emerald-100 text-emerald-700" : Number(p.margin_pct) >= 50 ? "bg-yellow-100 text-yellow-700" : "bg-red-100 text-red-700"}`}>{p.margin_pct}%</div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
          <div className="p-4 border-b"><h3 className="font-bold text-sm text-gray-700">Margem por Canal</h3></div>
          <div className="divide-y">
            {data.channels?.map((c, i) => (
              <div key={i} className="flex items-center px-4 py-2">
                <div className="flex-1"><p className="text-sm font-medium capitalize">{c.channel}</p><p className="text-xs text-gray-400">{c.orders} pedidos</p></div>
                <p className="text-sm font-bold text-emerald-600 mr-3">+{fmt(c.profit)}€</p>
                <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${Number(c.margin_pct) >= 65 ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{c.margin_pct}%</span>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
          <div className="p-4 border-b"><h3 className="font-bold text-sm text-gray-700">Margem por Horário</h3></div>
          <div className="divide-y max-h-64 overflow-y-auto">
            {data.hourly?.map((h, i) => (
              <div key={i} className="flex items-center px-4 py-2">
                <p className="text-sm font-medium w-14">{h.hour}</p>
                <div className="flex-1"><p className="text-xs text-gray-400">{h.orders} pedidos · {fmt(h.revenue)}€</p></div>
                <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${Number(h.margin_pct) >= 65 ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{h.margin_pct}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

